import React, { useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardHeader,
  Chip,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  ContactSupport as ContactSupportIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getContactUsCategories,
  createContactUs,
} from '../../endpoints/feedbackTs';
import { ContactUsFormData, ContactUsFormErrors } from '../../types/feedback';

const MAX_FEEDBACK_LENGTH = 1000;

export default function ContactUs(): React.JSX.Element {
  const [formData, setFormData] = useState<ContactUsFormData>({
    feedback: '',
    feedbackCategory: 0,
  });
  const [errors, setErrors] = useState<ContactUsFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch contact us categories
  const { data: contactUsCategories, isLoading: categoriesLoading } = useApiGet(
    ['contact-us-categories'],
    getContactUsCategories,
    { enabled: true }
  );

  const { mutate } = useApiSend(
    createContactUs,
    (data) => {
      setIsSaving(false);
      setShowSuccess(true);
      resetForm();
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    },
    (error) => {
      setErrors({
        general:
          error.message ||
          'Error submitting request. If the problem persists, please contact support.',
      });
      setIsSaving(false);
    }
  );

  const resetForm = (): void => {
    setFormData({
      feedback: '',
      feedbackCategory: 0,
    });
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: ContactUsFormErrors = {};

    if (!formData.feedback.trim()) {
      newErrors.feedback = 'Please provide your feedback or message';
    } else if (formData.feedback.length > MAX_FEEDBACK_LENGTH) {
      newErrors.feedback = `Feedback cannot exceed ${MAX_FEEDBACK_LENGTH} characters`;
    }

    if (!formData.feedbackCategory || formData.feedbackCategory === 0) {
      newErrors.feedbackCategory = 'Please select a category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof ContactUsFormData,
    value: string | number
  ): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (): void => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setErrors({});

    const submitData = {
      notes: formData.feedback.trim(),
      category_id: formData.feedbackCategory,
    };

    mutate(submitData);
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleSubmit();
    }
  };

  const remainingCharacters = MAX_FEEDBACK_LENGTH - formData.feedback.length;
  const isCharacterLimitExceeded = remainingCharacters < 0;

  if (categoriesLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardHeader
          avatar={
            <ContactSupportIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          }
          title={
            <Typography variant="h4" component="h1" color="primary">
              Contact Us
            </Typography>
          }
          subheader={
            <Typography variant="body1" color="text.secondary">
              We'd love to hear from you! Send us your feedback, questions, or
              suggestions.
            </Typography>
          }
        />
      </Card>

      {/* Success Alert */}
      {showSuccess && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            '& .MuiAlert-icon': {
              fontSize: '24px',
            },
            '& .MuiAlert-message': {
              fontSize: '16px',
              fontWeight: 500,
            },
          }}
          variant="filled"
        >
          Thank you for your message! We'll get back to you as soon as possible.
        </Alert>
      )}

      {/* Error Alert */}
      {errors.general && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            '& .MuiAlert-icon': {
              fontSize: '24px',
            },
            '& .MuiAlert-message': {
              fontSize: '16px',
              fontWeight: 500,
            },
          }}
          variant="filled"
        >
          {errors.general}
        </Alert>
      )}

      {/* Contact Form */}
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 3 }}>
          Send us a message
        </Typography>

        <Grid container spacing={3}>
          {/* Category Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.feedbackCategory}>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                value={formData.feedbackCategory}
                label="Category"
                onChange={(e) =>
                  handleInputChange(
                    'feedbackCategory',
                    e.target.value as number
                  )
                }
                disabled={isSaving}
              >
                <MenuItem value={0} disabled>
                  Select a category
                </MenuItem>
                {contactUsCategories
                  ?.sort((a, b) => a.name.localeCompare(b.name))
                  .map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
              </Select>
              {errors.feedbackCategory && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {errors.feedbackCategory}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Feedback Message */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <MessageIcon fontSize="small" />
                  <span>Your Message</span>
                </Stack>
              }
              value={formData.feedback}
              onChange={(e) => handleInputChange('feedback', e.target.value)}
              onKeyPress={handleKeyPress}
              error={!!errors.feedback}
              helperText={
                errors.feedback || (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <span>
                      Share your thoughts, questions, or feedback with us
                    </span>
                    <Chip
                      label={`${remainingCharacters} chars left`}
                      size="small"
                      color={
                        isCharacterLimitExceeded
                          ? 'error'
                          : remainingCharacters < 100
                            ? 'warning'
                            : 'default'
                      }
                      variant={
                        remainingCharacters < 100 ? 'filled' : 'outlined'
                      }
                    />
                  </Stack>
                )
              }
              disabled={isSaving}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">
                      Ctrl+Enter to submit
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={isSaving}
                size="large"
              >
                Clear Form
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  isSaving ||
                  !formData.feedback.trim() ||
                  !formData.feedbackCategory ||
                  formData.feedbackCategory === 0
                }
                startIcon={
                  isSaving ? <CircularProgress size={20} /> : <SendIcon />
                }
                size="large"
                sx={{ minWidth: 140 }}
              >
                {isSaving ? 'Sending...' : 'Send Message'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Help Text */}
      <Paper elevation={1} sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Need immediate assistance? For urgent matters, please contact our
          support team directly.
          <br />
          We typically respond to all messages within 24-48 hours during
          business days.
        </Typography>
      </Paper>
    </Box>
  );
}
